import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Zap, ArrowRight } from 'lucide-react';

/**
 * FastByNoFeeHowItWorksModal — single source of truth for the
 * "How Fast by NoFee Works" explanation. Used by both the landing-page
 * Fast section ("Learn more" + "How it works") and the
 * For-Restaurants page benefit row, so the messaging is always:
 *
 *   Fast by NoFee does NOT mean fastest delivery.
 *   It means On-Time Delivery Verified by real customers.
 *
 * Public/visitor-friendly. Owners get a clear "Start Restaurant Signup"
 * CTA → /start?plan=ordering at the bottom.
 */
const STEPS = [
  { n: 1, text: 'Restaurant owner monitors delivery performance in real time. Receive customer-verified delivery confirmations and track operational performance from anywhere.' },
  { n: 2, text: 'Restaurant sets pickup and delivery times — editable anytime (e.g. today 45 min, tomorrow 30 min).' },
  { n: 3, text: 'Customer places an order.' },
  { n: 4, text: 'After delivery, the customer confirms whether the order arrived within the promised time — Yes (on time) or No (late).' },
  { n: 5, text: 'Verified on-time deliveries help your restaurant earn customer trust through Fast by NoFee.' },
];

const FastByNoFeeHowItWorksModal = ({ open, onClose, ctaSource = 'fast_modal' }) => {
  // Close on Escape
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    // Lock background scroll while modal is open
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      style={{ width: '100vw', height: '100dvh' }}
      onClick={onClose}
      data-testid="fast-how-it-works-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fast-modal-title"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full sm:max-w-xl bg-gradient-to-b from-zinc-950 to-black border border-yellow-400/20 rounded-3xl overflow-y-auto"
        style={{ maxHeight: 'calc(100dvh - 32px)' }}
      >
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 sm:px-8 pt-5 sm:pt-7 pb-3 bg-gradient-to-b from-zinc-950 to-zinc-950/95 backdrop-blur">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-yellow-400/15 border border-yellow-400/30 text-white text-[11px] font-black uppercase tracking-[0.18em]">
            <Zap className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            Fast by NoFee
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="fast-modal-close"
            aria-label="Close"
            className="w-9 h-9 rounded-full border border-white/10 hover:border-white/25 hover:bg-white/[0.04] flex items-center justify-center text-white/75 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 sm:px-8 pb-7 sm:pb-9">
          {/* Official badge identity at the top */}
          <div className="flex items-start gap-3">
            <span className="w-12 h-12 rounded-2xl bg-yellow-400/15 border border-yellow-400/35 flex items-center justify-center flex-shrink-0">
              <Zap className="w-6 h-6 text-yellow-400 fill-yellow-400" />
            </span>
            <div className="min-w-0">
              <p className="text-white text-[11px] font-black uppercase tracking-[0.18em]">
                ⚡ Fast by NoFee
              </p>
              <h2
                id="fast-modal-title"
                className="text-2xl sm:text-3xl font-black text-white leading-tight mt-1"
              >
                On-Time Delivery Verified
              </h2>
              <p className="text-[12.5px] text-white/65 mt-1.5">How Fast by NoFee works</p>
            </div>
          </div>

          {/* Disclaimer / clarification */}
          <div className="mt-5 p-3.5 rounded-xl border border-yellow-400/20 bg-yellow-400/[0.06]">
            <p className="text-[13.5px] text-white/85 leading-relaxed">
              <strong className="text-yellow-300">Fast by NoFee does not mean fastest delivery.</strong>
              <br />
              Fast by NoFee means <span className="text-white font-semibold">On-Time Delivery Verified</span> by real customers.
            </p>
          </div>

          {/* Steps */}
          <ol className="mt-6 space-y-3.5">
            {STEPS.map((s) => (
              <li
                key={s.n}
                className="flex items-start gap-3.5"
                data-testid={`fast-step-${s.n}`}
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-full bg-yellow-400 text-zinc-950 font-black text-sm flex items-center justify-center">
                  {s.n}
                </span>
                <p className="text-[14.5px] text-white/85 leading-relaxed pt-[3px]">
                  {s.text}
                </p>
              </li>
            ))}
          </ol>

          {/* CTAs */}
          <div className="mt-7 flex flex-col sm:flex-row gap-2.5">
            <Link
              to={`/register?next=onboarding&utm_source=${encodeURIComponent(ctaSource)}`}
              onClick={onClose}
              data-testid="fast-modal-cta-signup"
              className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-yellow-400 hover:bg-yellow-300 text-zinc-950 font-bold text-sm transition-colors"
            >
              Start Earning Fast Verifications
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/for-restaurants/why-nofee"
              onClick={onClose}
              data-testid="fast-modal-cta-learn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl border border-white/15 hover:border-white/30 text-white/85 hover:text-white font-bold text-sm transition-colors"
            >
              Learn more
            </Link>
          </div>
          <p className="mt-3 text-[11.5px] text-white/45 text-center">
            Complete onboarding first · 45-day free trial · 0% commission · No contracts.
          </p>
        </div>
      </div>
    </div>
  );
};

export default FastByNoFeeHowItWorksModal;
